// Core imports
import React, { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';

// Component imports
import { Button, Card, Col, Row, Typography } from 'antd';
import { Send, Message, ArrowUpSquare, Wallet, Chart } from 'react-iconly';
import SEO from '../components/SEO/SEO';

// Misc imports
import joinUsImg from '../assets/images/join-us.png';
import { NAV_DONATE, NAV_NDES_CREATE, FACEBOOK_URL, SUPPORT_EMAIL } from '../constants';

// Constant declarations
const { Title, Paragraph } = Typography;

const iconCircleStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 64,
  height: 64,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, rgba(145, 149, 252, 0.12), rgba(191, 193, 255, 0.12))',
  marginBottom: 16
};

const cardBodyStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  height: '100%',
  padding: '32px 28px'
};

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://ndearchive.com';

const JoinUsScreen = () => {
  const navigate = useNavigate();

  return (
    <Fragment>
      <SEO
        title="Get Involved | Share Your NDE or Contribute"
        description="Join the NDE Archive community. Share your near-death experience, spread the word, contribute to research, or support the project."
        path="/join-us"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Get Involved | NDE Archive',
            description: 'Join the NDE Archive community. Share your near-death experience, spread the word, contribute to research, or support the project.',
            url: `${SITE_URL}/join-us`
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: 'Get Involved', item: `${SITE_URL}/join-us` }
            ]
          }
        ]}
      />
      <Row justify="center" align="middle" className="mb-48">
        <Col xs={24} md={10}>
          <img src={joinUsImg} alt="Get Involved" style={{ maxWidth: '100%', height: 'auto', filter: 'drop-shadow(0 8px 30px rgba(0,0,0,0.08))' }} />
        </Col>

        <Col xs={24} md={14}>
          <Title level={1} style={{ color: '#1a1a2e', fontWeight: 800, marginBottom: 24, letterSpacing: '-0.5px' }}>Get Involved</Title>
          <Paragraph style={{ color: '#555', fontSize: 18, lineHeight: '30px', marginBottom: 24 }}>
            Anyone who is <strong>passionate about near-death experiences (NDEs)</strong> is welcome to contribute to the platform.
            <br/><br/>
            {FACEBOOK_URL ? (
              <><a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer">Follow NDE Archive on Facebook</a> to stay updated on the latest news and content. Sharing posts helps spread the word about the platform. <strong>Your support is greatly appreciated!</strong></>
            ) : (
              <>Follow NDE Archive on Facebook to stay updated on the latest news and content. Sharing posts helps spread the word about the platform. <strong>Your support is greatly appreciated!</strong></>
            )}
            <br/><br/>
            <strong>Sincere gratitude</strong> to all contributors and supporters. Together, it's possible to continue advancing the understanding of near-death experiences and their transformative impact on human lives.
          </Paragraph>
        </Col>
      </Row>

      <Row gutter={[32, 32]} align="stretch" justify="center" className="mb-64">
        <Col xs={24} sm={12} md={8}>
          <Card
            hoverable
            className="h-100 elevation text-center hover-elevation-2"
            bordered={false}
            style={{ borderRadius: 16, overflow: 'hidden' }}
            bodyStyle={cardBodyStyle}
          >
            <div style={iconCircleStyle}>
              <Send set="bulk" className="text-primary" size={32} />
            </div>
            <Title level={3} style={{ marginBottom: 12 }}>Share Your Experience</Title>
            <Paragraph style={{ color: '#555', fontSize: 15, lineHeight: '26px', flexGrow: 1, marginBottom: 24 }}>
              Share your own NDE by visiting the "Share your experience" page and filling out the submission form. By sharing your experience, you contribute to the growing database and help others gain a better understanding of NDEs.
            </Paragraph>
            <Button type="primary" size="large" style={{ borderRadius: 8, minWidth: 160 }} onClick={() => navigate(NAV_NDES_CREATE)}>
              Share Your NDE
            </Button>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Card
            hoverable
            className="h-100 elevation text-center hover-elevation-2"
            bordered={false}
            style={{ borderRadius: 16, overflow: 'hidden' }}
            bodyStyle={cardBodyStyle}
          >
            <div style={iconCircleStyle}>
              <Chart set="bulk" className="text-primary" size={32} />
            </div>
            <Title level={3} style={{ marginBottom: 12 }}>Collaborate on Research</Title>
            <Paragraph style={{ color: '#555', fontSize: 15, lineHeight: '26px', flexGrow: 1, marginBottom: 24 }}>
              Researchers are invited to use the data available on the platform for their studies. Collaborate with other researchers in the field of near-death experiences and help advance the understanding of this intriguing phenomenon.
            </Paragraph>
            {SUPPORT_EMAIL && (
              <Button type="primary" size="large" style={{ borderRadius: 8, minWidth: 160 }} onClick={() => {
                window.location.href = `mailto:${SUPPORT_EMAIL}`;
              }}>
                Get in Touch
              </Button>
            )}
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Card
            hoverable
            className="h-100 elevation text-center hover-elevation-2"
            bordered={false}
            style={{ borderRadius: 16, overflow: 'hidden' }}
            bodyStyle={cardBodyStyle}
          >
            <div style={iconCircleStyle}>
              <Message set="bulk" className="text-primary" size={32} />
            </div>
            <Title level={3} style={{ marginBottom: 12 }}>Provide Feedback</Title>
            <Paragraph style={{ color: '#555', fontSize: 15, lineHeight: '26px', flexGrow: 1, marginBottom: 24 }}>
              Your input is valuable! Feel free to provide feedback on the platform. Suggest new features and improvements to help create a better experience for all users.
            </Paragraph>
            {SUPPORT_EMAIL && (
              <Button type="primary" size="large" style={{ borderRadius: 8, minWidth: 160 }} onClick={() => {
                window.location.href = `mailto:${SUPPORT_EMAIL}`;
              }}>
                Send Feedback
              </Button>
            )}
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Card
            hoverable
            className="h-100 elevation text-center hover-elevation-2"
            bordered={false}
            style={{ borderRadius: 16, overflow: 'hidden' }}
            bodyStyle={cardBodyStyle}
          >
            <div style={iconCircleStyle}>
              <ArrowUpSquare set="bulk" className="text-primary" size={32} />
            </div>
            <Title level={3} style={{ marginBottom: 12 }}>Support Us on Social Media</Title>
            <Paragraph style={{ color: '#555', fontSize: 15, lineHeight: '26px', flexGrow: 1, marginBottom: 24 }}>
              Follow on social media and share content with your friends and followers. Help raise awareness about the platform and reach a wider audience.
            </Paragraph>
            {FACEBOOK_URL && (
              <Button type="primary" size="large" style={{ borderRadius: 8, minWidth: 160 }} onClick={() => {
                window.open(FACEBOOK_URL, '_blank', 'noopener,noreferrer');
              }}>
                Follow on Facebook
              </Button>
            )}
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Card
            hoverable
            className="h-100 elevation text-center hover-elevation-2"
            bordered={false}
            style={{ borderRadius: 16, overflow: 'hidden' }}
            bodyStyle={cardBodyStyle}
          >
            <div style={iconCircleStyle}>
              <Wallet set="bulk" className="text-primary" size={32} />
            </div>
            <Title level={3} style={{ marginBottom: 12 }}>Donate</Title>
            <Paragraph style={{ color: '#555', fontSize: 15, lineHeight: '26px', flexGrow: 1, marginBottom: 24 }}>
              Your donations support the mission and help maintain and improve the platform. With your assistance, the project can continue advancing the understanding of NDEs and their transformative impact on human lives.
            </Paragraph>
            <Button type="primary" size="large" style={{ borderRadius: 8, minWidth: 160 }} onClick={() => navigate(NAV_DONATE)}>
              Donate Now
            </Button>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default JoinUsScreen;
